import { useMemo } from "react";
import {
  format,
  startOfMonth,
  startOfWeek,
  addDays,
  isSameMonth,
  isToday,
  isSameDay,
  subMonths,
  addMonths,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { cn, formatMonth } from "../../lib/utils";
import { EventFragment, ActivityFragment } from "../../__generated__/graphql";

const DAYS_OF_WEEK = ["L", "M", "M", "J", "V", "S", "D"];

interface InteractiveCalendarProps {
  onDaySelect?: (date: Date) => void;
  selectedDate?: Date;
  className?: string;
  events?: EventFragment[];
  activities?: ActivityFragment[];
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
}

export default function InteractiveCalendar({
  onDaySelect,
  selectedDate,
  className,
  events = [],
  activities = [],
  currentMonth,
  onMonthChange,
}: InteractiveCalendarProps) {
  const firstDayOfMonth = startOfMonth(currentMonth);
  const firstDayOfCalendar = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });

  const dates = useMemo(() => {
    return [...Array(42)].map((_, index) => {
      const date = addDays(firstDayOfCalendar, index);
      return {
        date,
        isCurrentMonth: isSameMonth(date, currentMonth),
      };
    });
  }, [firstDayOfCalendar, currentMonth]);

  const handleMonthChange = (actions: "previous" | "next") => {
    if (actions === "previous") {
      onMonthChange(subMonths(currentMonth, 1));
    } else {
      onMonthChange(addMonths(currentMonth, 1));
    }
  };

  const handleDayClick = (date: Date) => {
    // If clicking a day from a different month, navigate to that month
    if (!isSameMonth(date, currentMonth)) {
      onMonthChange(date);
    }
    onDaySelect?.(date);
  };

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventFragment[]>();
    events.forEach((event) => {
      if (!event) return;
      const eventDate = new Date(event.startDate);
      const dateKey = format(eventDate, "yyyy-MM-dd");
      const existing = map.get(dateKey) || [];
      existing.push(event);
      map.set(dateKey, existing);
    });
    return map;
  }, [events]);

  // Group activities by date
  const activitiesByDate = useMemo(() => {
    const map = new Map<string, ActivityFragment[]>();
    activities.forEach((activity) => {
      if (!activity) return;
      const activityDate = new Date(activity.startDate);
      const dateKey = format(activityDate, "yyyy-MM-dd");
      const existing = map.get(dateKey) || [];
      existing.push(activity);
      map.set(dateKey, existing);
    });
    return map;
  }, [activities]);

  // Get dots for a specific date (max 4 total: events first, then activities)
  const getDotsForDate = (date: Date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const dayEvents = eventsByDate.get(dateKey) || [];
    const dayActivities = activitiesByDate.get(dateKey) || [];

    const dots: Array<{ type: "event" | "activity"; color?: string }> = [];

    // Add event dots (accent color)
    const eventCount = Math.min(dayEvents.length, 4);
    for (let i = 0; i < eventCount && dots.length < 4; i++) {
      dots.push({ type: "event" });
    }

    // Add activity dots (with their colors)
    const remainingSlots = 4 - dots.length;
    const activityCount = Math.min(dayActivities.length, remainingSlots);
    for (let i = 0; i < activityCount; i++) {
      dots.push({
        type: "activity",
        color: dayActivities[i].color,
      });
    }

    return dots;
  };

  return (
    <div
      className={cn(
        "bg-dark border border-input rounded-lg p-3 flex flex-col gap-2 w-fit",
        className
      )}
    >
      {/* Header with month navigation */}
      <div className="flex items-center justify-between min-w-[240px]">
        <button
          onClick={() => handleMonthChange("previous")}
          className="p-1 rounded-md hover:bg-input transition-colors flex-shrink-0 cursor-pointer"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="w-4 h-4 text-primary" />
        </button>

        <h3 className="text-base font-semibold text-primary text-center flex-1">
          {formatMonth(currentMonth)}
        </h3>

        <button
          onClick={() => handleMonthChange("next")}
          className="p-1 rounded-md hover:bg-input transition-colors flex-shrink-0 cursor-pointer"
          aria-label="Next month"
        >
          <ChevronRightIcon className="w-4 h-4 text-primary" />
        </button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 w-[240px]">
        {DAYS_OF_WEEK.map((day, index) => (
          <div
            key={index}
            className="text-[10px] font-semibold text-secondary text-center py-0.5"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 w-[240px]">
        {dates.map(({ date, isCurrentMonth }) => {
          const isSelected = selectedDate && isSameDay(date, selectedDate);
          const isTodayDate = isToday(date);
          const dots = getDotsForDate(date);

          return (
            <button
              key={date.toISOString()}
              onClick={() => handleDayClick(date)}
              className={cn(
                "aspect-square rounded-md text-xs font-medium transition-all duration-200 cursor-pointer hover:bg-subCard relative flex flex-col items-center justify-center",
                isCurrentMonth ? "text-primary" : "text-secondary opacity-50",
                isTodayDate &&
                  !isSelected &&
                  "ring-1 ring-border ring-offset-1 ring-offset-card ",
                isSelected &&
                  "bg-accent text-background font-bold hover:bg-accent"
              )}
            >
              <span>{format(date, "d")}</span>
              {dots.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {dots.map((dot, i) => (
                    <div
                      key={i}
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: isSelected
                          ? "var(--color-background)"
                          : dot.type === "event"
                          ? "var(--color-accent)"
                          : dot.color || "var(--color-accent)",
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
